import React, { useContext, useMemo } from 'react';

import { PostOfficeProvider as PostOfficeBaseProvider } from '@atlassian/post-office-context/post-office';
import { usePostOfficeRouteUpdate } from '@atlassian/post-office-context/listener';

import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { FlagToShareActionsProvider } from '@confluence/flag-to-share-actions/entry-points/flag-to-share-actions.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	sessionToPostOfficeContext,
	transformConfluenceEnvToPostOfficeEnvironment,
} from './session-to-post-office-context.ts';
import { ConfluenceExperimentsProvider } from './confluence-experiments-context.tsx';
import { PostOfficeAnalyticsListener } from './post-office-analytics-listener.tsx';

const ConfluencePostOfficeProvider = ({ children }: { children: React.ReactNode }) => {
	const { accessStatus, activationId, cloudId, environment, locale, orgId, edition } =
		useSessionData();

	const context = useMemo(
		() => ({
			current: sessionToPostOfficeContext({
				accessStatus,
				activationId,
				cloudId,
				locale,
				orgId,
				edition,
			}),
		}),
		[activationId, cloudId, accessStatus, locale, orgId, edition],
	);

	const env = useMemo(
		() => ({ env: transformConfluenceEnvToPostOfficeEnvironment(environment) }),
		[environment],
	);

	const analyticsClientInstance = useMemo(() => getAnalyticsWebClient(), []);

	return (
		<PostOfficeBaseProvider
			context={context}
			environment={env}
			analyticsClientInstance={analyticsClientInstance}
		>
			<PostOfficeAnalyticsListener>{children}</PostOfficeAnalyticsListener>
		</PostOfficeBaseProvider>
	);
};

export const PostOfficeProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
	return (
		<FlagToShareActionsProvider>
			<ConfluencePostOfficeProvider>
				<ConfluenceExperimentsProvider>{children}</ConfluenceExperimentsProvider>
			</ConfluencePostOfficeProvider>
		</FlagToShareActionsProvider>
	);
};

const ConfluenceRouterListener = ({ children }: { children: React.ReactNode }) => {
	const routeContext = useContext(RoutesContext);

	usePostOfficeRouteUpdate([routeContext.getHref() ?? '', routeContext.match?.name ?? '']);

	return <>{children}</>;
};

export const PostOfficeRouterListener = ({
	children,
}: {
	children: React.ReactNode;
}): JSX.Element => {
	return <ConfluenceRouterListener>{children}</ConfluenceRouterListener>;
};
